import { useEffect, useRef, useState } from 'react';
import parseZone from 'helpers/parseZone';
import { FormProvider, useForm } from 'react-hook-form';
import BookingInfoItem from 'components/messaging/BookingInfoItem';
import { dollarsNoCentsWithSymbol } from 'helpers/currency';
import CaptainDeclineContent from 'components/modals/content/CaptainDeclineContent';
import CaptainApproveContent from 'components/modals/content/CaptainApproveContent';
import CaptainCancelContent from 'components/modals/content/CaptainCancelContent';
import { useBreakpoint } from 'components/BreakpointProvider';
import { useRouter } from 'next/router';
import getServerSidePropsImported from 'components/checkout/getServerSideProps';
import { useBookingTrip } from 'components/booking/queryHooks';
import { useAtom } from 'jotai';
import { bookingActionDoneAtom } from 'components/booking/atoms';
import {
  useApproveCaptainForBookingMutation,
  useCancelCaptainForBookingMutation,
  useDeclineCaptainForBookingMutation,
} from 'components/booking/mutationHooks';
import { useCloseModal } from 'components/remodal/v2/hooks';

const CaptainBookingActionsModal = ({ booking: bookingModal, actionType: actionTypeModal, onSuccess }) => {
  const closeModal = useCloseModal();
  const [bookingActionDone, setBookingActionDone] = useAtom(bookingActionDoneAtom);
  const { bookingTrip } = useBookingTrip();

  const router = useRouter();
  const params = router.query;
  const isFramed = router.asPath.includes('captain_action');

  const approveCaptainForBookingMutation = useApproveCaptainForBookingMutation();
  const declineCaptainForBookingMutation = useDeclineCaptainForBookingMutation();
  const cancelCaptainForBookingMutation = useCancelCaptainForBookingMutation();

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  const [loading, setLoading] = useState(false);
  const contentRef = useRef();

  const methods = useForm({
    mode: 'onChange',
  });
  const { handleSubmit } = methods;

  useEffect(() => {
    resizeIframe();
  }, []);

  const booking = isFramed ? bookingTrip : bookingModal;
  const action = params?.action;
  const bookingParamId = params?.bookingId;
  const actionType = isFramed ? action : actionTypeModal;
  const showBookingInfo = actionType === 'approve';
  const bookingId = booking?.id || bookingParamId;

  const {
    trip_start,
    trip_finish,
    package_type,
    package_price,
    date_price_adjustment,
    insurance_type,
    captain_payout,
    passengers,
  } = booking;

  const modalIdReference = () => {
    if (actionType === 'approve') {
      return `#js-captain-approve-modal-${booking.id}`;
    }
    if (actionType === 'cancel') {
      return `#js-captain-cancel-booking-modal-${booking.id}`;
    }
    if (actionType === 'decline') {
      return `#js-captain-decline-booking-modal-${booking.id}`;
    }
  };

  const processFormData = async (formValues) => {
    if (loading) {
      return;
    }
    const { reason_text, category } = formValues;

    setLoading(true);
    let reasonDecline = category;
    if (reason_text) {
      reasonDecline = `Other: ${reason_text}`;
    }

    if (actionType === 'decline') {
      await declineCaptainForBookingMutation.mutateAsync({ bookingId: booking.id, category: reasonDecline });
    } else if (actionType === 'cancel') {
      await cancelCaptainForBookingMutation.mutateAsync({ bookingId: booking.id, category: reasonDecline });
    } else if (actionType === 'approve') {
      await approveCaptainForBookingMutation.mutateAsync({ bookingId: booking.id });
    }

    if (!isFramed) {
      if (onSuccess) onSuccess();
    } else {
      setBookingActionDone(true);
    }

    setLoading(false);
  };

  const handleCloseModal = () => {
    closeModal?.();
    setBookingActionDone(false);
    if (window?.parent?.remodalClose) {
      window?.parent?.remodalClose(modalIdReference());
      window?.parent?.location?.reload();
    }
  };

  const diffHours = parseZone(trip_finish).diff(parseZone(trip_start), 'hour', true);

  const infoItems = [
    {
      label: 'Date',
      value: `${parseZone(trip_start).format('MMM DD, YYYY')}`,
    },
    {
      label: 'Start time',
      value: `${parseZone(trip_start).format('hh:mm A')}`,
    },
    {
      label: 'Duration',
      value: diffHours >= 24 ? `${Math.ceil(diffHours / 24)} days` : `${diffHours} hours`,
    },
    {
      label: 'Captain',
      value: package_type === 'captained' ? 'Captained' : 'No captain',
    },
    {
      label: 'Passengers',
      value: passengers,
    },
  ].filter((b) => !!b.value);

  const resizeIframe = (extraSpace) => {
    const modalHeight = contentRef?.current?.offsetHeight;
    if (isFramed && window.parent && window.parent.resizeIframeModal) {
      const space = extraSpace || 0;
      window.parent.resizeIframeModal(modalIdReference(), modalHeight + space);
    } else {
      const windowHeight = window.innerHeight;
      const animateHeight = 100 - (modalHeight * 100) / windowHeight;
      const extra_space = extraSpace || 0;
      const space = extra_space + (smallScreen ? 0 : 70);
      document.body.setAttribute(
        'style',
        `--modal-height-offset: ${animateHeight}vh; --modal-content-height: ${modalHeight + space}px`
      );
    }
  };

  const content = () => {
    if (actionType === 'decline') {
      return (
        <CaptainDeclineContent
          hideModal={handleCloseModal}
          insuranceType={insurance_type}
          bookingId={bookingId}
          loading={loading}
          isCancel
          resizeIframe={resizeIframe}
          isFramed={isFramed}
        />
      );
    }
    if (actionType === 'approve') {
      return <CaptainApproveContent hideModal={handleCloseModal} insuranceType={insurance_type} booking={booking} />;
    }
    if (actionType === 'cancel') {
      return (
        <CaptainCancelContent
          hideModal={handleCloseModal}
          insuranceType={insurance_type}
          resizeIframe={resizeIframe}
          isFramed={isFramed}
        />
      );
    }
  };

  return (
    <div style={{ minHeight: smallScreen ? 'auto' : 435 }} className="w-100" ref={contentRef}>
      {bookingActionDone ? (
        <div className={`u-p1_5 ${!isFramed ? 'u-sm-p0' : ''}`}>
          <div style={{ height: '45vh' }} className="u-flex u-flexJustifyCenter u-flexAlignItemsStart u-textCenter">
            <div className="Grid">
              <div className="Grid-cell Grid-cell u-flex u-flexAlignItemsCenter u-flexCol">
                <img src="/images/done.svg" alt="Done" className="u-mt1 u-mb2" />
                <h2 className="u-fsXl u-textSemiBold u-mb1">
                  {actionType === 'special-offer'
                    ? 'You have Sent a special offer for this booking'
                    : `This booking has been ${actionType === 'cancel' ? 'cancele' : actionType}d`}
                </h2>
                <button
                  className="Button Button--primary Button--sticky u-textBold u-textUppercase u-flex u-flexAlignItemsCenter u-flexJustifyCenter u-mb1"
                  type="button"
                  onClick={handleCloseModal}
                  style={{ minWidth: 175 }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={`u-p1_5 ${!isFramed ? 'u-sm-p0' : ''}`}>
          <div className="Grid ">
            <div className="Grid-cell">
              <div className="u-mb1">
                <h1
                  className={`u-fsXl u-textSemiBold u-mb1 ${
                    isFramed ? 'isFramedTray' : ''
                  } remodalTrayTitleMobile u-textCapitalizeFirstLetter`}
                >
                  {actionType} booking
                </h1>

                {(actionType === 'decline' || actionType === 'cancel') && (
                  <p className={`u-mb1 ${isFramed ? 'isFramedTray' : ''} remodalTraySpacingMobile`}>
                    If you need additional help please email&nbsp;
                    <a className="u-textCaptains" href="mailto:contact@boatsetter.com">
                      contact@boatsetter.com
                    </a>{' '}
                    for assistance.
                  </p>
                )}
              </div>
            </div>
          </div>

          {showBookingInfo && (
            <>
              <div className={`u-fsBase u-textBold u-mb1 ${isFramed ? 'isFramedTray' : ''} remodalTraySpacingMobile`}>
                Details
              </div>
              <div className="Grid Grid--withGutterWide Grid-sm--withGutterWideNoMargin  Grid--alignMiddle">
                <div className="Grid-cell Grid-cell-sm--noGutter u-md-size1of2 u-lg-size1of2 u-sm-mb1">
                  {infoItems.map((item) => (
                    <BookingInfoItem key={item.label} label={item.label} value={item.value} />
                  ))}
                </div>
                <div className="Grid-cell Grid-cell-sm--noGutter u-md-size1of2 u-lg-size1of2 u-flexAlignItemsCenter">
                  <div className="Panel Panel--light Panel--semiCompact u-mb2 u-block Panel--border Panel--primary Panel--stretch">
                    <div className="Grid Grid--alignMiddle u-mb05">
                      <div className="Grid-cell u-size1of3">
                        <div className="u-fsSmMd">Boat price</div>
                      </div>
                      <div className="Grid-cell u-size2of3">
                        <div className="u-fsSmMd u-textRight u-textSemiBold">
                          {dollarsNoCentsWithSymbol(package_price + date_price_adjustment)}
                        </div>
                      </div>
                    </div>
                    <div className="Grid Grid--alignMiddle u-mb05">
                      <div className="Grid-cell u-size1of3">
                        <div className="u-fsSmMd u-textBold">You earn</div>
                      </div>
                      <div className="Grid-cell u-size2of3">
                        <div className="u-fsSmMd u-textRight u-textBold u-textSuccess">
                          {dollarsNoCentsWithSymbol(captain_payout)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr className="Divider u-mt2 u-mb2" />
            </>
          )}
          <div className="Grid">
            <div className="Grid-cell">
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(processFormData)}>{content()}</form>
              </FormProvider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const getServerSideProps = getServerSidePropsImported;

export default CaptainBookingActionsModal;
